/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import React, { useState, useContext, useEffect, useRef } from 'react'
import {
  UnitSelectionContext,
  DatePickerContext,
  SpinContext,
  ErrorContext,
} from 'context'
import { UnitSelection, getApiTargetInfo, ExportComponent } from 'core'
import { DatePicker, Button, Input, Empty, message, Checkbox } from 'antd'
import { getPjmLevel2ValidationStatus } from 'api/pgads'

import { connect } from 'react-redux'
import { CriteriaRow, Title, Content, FullHeightSizer } from 'core/screen-util'
import {
  utilityHeader,
  unitHeader,
  modifierGetter,
  modifiedDateGetter,
} from 'core/HeaderColTypeProps'
import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import { strCol, intCol } from '@integec/grid-tools/lib/cols'
import {
  rowHeaderRender,
  exportDataComponent,
  isValidYearRange,
} from 'core/utils'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUnits,
  getValidYearRange,
} from 'core/selectors'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'

import R from 'ramda'

import { statusRender } from './TabValidationScreen'
const { MonthPicker } = DatePicker
export const PJMStatusValidationHeaders = [
  intCol({
    ident: 'year',
    display: 'Year',
    isKey: true,
  }),
  intCol({
    ident: 'month',
    display: 'Month',
    isKey: true,
  }),
  utilityHeader,
  unitHeader,
  strCol({
    ident: 'status',
    display: 'Status',
    width: 200,
  }),
  {
    ident: 'modifier',
    display: 'Validated By',
    dataGetter: modifierGetter,
    showInRowEditor: false,
    type: 'str',
    width: 130,
    isKey: true,
  },
  {
    ident: 'modifiedDate',
    display: 'Validated Date',
    dataGetter: modifiedDateGetter,
    width: 200,
    showInRowEditor: false,
    dataFormat: 'YYYY-MM-DD[T]HH:mm:SS.sssZZ',
    displayFormat: 'MM/DD/YYYY HH:mm:SS',
    type: 'date-time',
    isKey: true,
  },
  strCol({
    ident: 'lastFailedAttempt',
    display: 'Last Failed Attempt',
    width: 200,
  }),
  strCol({
    ident: 'last05Added',
    display: 'Last 05 Added',
    width: 200,
  }),
  strCol({
    ident: 'last05Modified',
    display: 'Last 05 Mod/Deleted',
    width: 200,
  }),
  strCol({
    ident: 'last07Added',
    display: 'Last 07 Added',
    width: 200,
  }),
  strCol({
    ident: 'last07Modified',
    display: 'Last 07 Mod/Deleted',
    width: 200,
  }),
]
const PjmLevel2ValStatus = ({
  apiTargetInfo,
  utilityNameMap,
  unitNameMap,
  ...props
}) => {
  // const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [dataFiltered, setDataFiltered] = useState([])
  const [fuzzyFilter, setFuzzyFilter] = useState('')
  const { selectedUnits, setVisibleUnitSelection } = useContext(
    UnitSelectionContext
  )
  const { selectedDateRangePicker, setSelectedDateRagePicker } = useContext(
    DatePickerContext
  )
  // const [err, setError] = useState(null)
  const { spinning, setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [showErrorOnly, setShowErrorOnly] = useState(false)
  useEffect(
    _ => {
      if (showErrorOnly) {
        setDataFiltered(data.filter(f => f.status === 'Not Validated'))
      } else {
        setDataFiltered(data)
      }
    },
    [data, showErrorOnly]
  )
  const retrieveTask = ({ selUnits = selectedUnits }) => {
    if (selUnits.length <= 0) {
      message.warning('Please,Select Units')
      setVisibleUnitSelection(true)
      return
    }

    const isValid = isValidYearRange(
      props.validYearRange,
      selectedDateRangePicker[0],
      selectedDateRangePicker[1]
    )
    if (!isValid) return

    setSpinning(true)
    getPjmLevel2ValidationStatus(apiTargetInfo)(
      selectedDateRangePicker[0].format('YYYY'),
      selectedDateRangePicker[0].format('MM'),
      selectedDateRangePicker[1].format('MM'),
      selectedDateRangePicker[1].format('YYYY'),
      R.uniq(selUnits.map(u => u.utility)),
      selUnits.map(u => u.utility + u.unit)
    ).fork(setError, data => {
      setSpinning(false)
      setData(data)
      setDataFiltered(data)
      setShowErrorOnly(false)
    })
  }
  const title = 'Level 2 Validation Status'
  const headerWithFormatter = PJMStatusValidationHeaders.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          ...toSelectionColProps(utilityNameMap),
          dataFormatter: ({ value }) => utilityNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
        }
      : header.ident === 'unit'
      ? {
          ...header,
          ...toSelectionColProps(unitNameMap),
          dataFormatter: ({ value }) => unitNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            unitNameMap[rowData.unit] || rowData.unit,
        }
      : header
  )

  const GridRef = useRef()

  return (
    <div
      style={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <UnitSelection setData={() => setData([])} retrieveData={retrieveTask} />
      <CriteriaRow>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
          }}
        >
          {title}
        </Title>
        <MonthPicker
          value={selectedDateRangePicker[0]}
          format="MMM-YYYY"
          picker="month"
          onChange={date => {
            const endDate = date.isAfter(selectedDateRangePicker[1])
              ? date
              : selectedDateRangePicker[1]
            const range = [date.startOf('month'), endDate.endOf('month')]
            setSelectedDateRagePicker(range)
            setData([])
          }}
          allowClear={false}
        />
        <div
          style={{
            marginLeft: '5px',
            marginRight: '5px',
            display: 'grid',
            alignItems: 'center',
          }}
        >
          To
        </div>
        <MonthPicker
          value={selectedDateRangePicker[1]}
          format="MMM-YYYY"
          picker="month"
          onChange={date => {
            const stDate = date.isBefore(selectedDateRangePicker[0])
              ? date
              : selectedDateRangePicker[0]
            const range = [stDate.startOf('month'), date.endOf('month')]
            setSelectedDateRagePicker(range)
            setData([])
          }}
          allowClear={false}
        />
        <Button
          loading={spinning}
          onClick={retrieveTask}
          style={{ marginLeft: '10px' }}
          type="primary"
          icon="database"
        >
          Retrieve
        </Button>
        <div css={{ flex: '1 1 0px' }} />
        <Checkbox
          checked={showErrorOnly}
          onChange={e => setShowErrorOnly(e.target.checked)}
        >
          Only show not validated
        </Checkbox>
        <div css={{ flex: '1 1 0px' }} />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          css={{ marginLeft: '10px', marginRight: '10px', flex: '0 0 200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <ExportComponent
          getData={_ => exportDataComponent(GridRef, data, fuzzyFilter)}
          data={data}
          headers={headerWithFormatter.map(d => ({
            ...d,
            rowHeaderBackgroundColor: '#eee',
          }))}
          fileName={title}
          btnStyle={{ marginRight: '10px' }}
          disabled={data.length <= 0}
        />
      </CriteriaRow>
      <FullHeightSizer heightOffset={250}>
        {({ height, width }) => {
          return data && data.length > 0 ? (
            <Content style={{ height: height - 20 }}>
              <Grid
                ref={GridRef}
                sortEnabled
                isEditable={false}
                data={dataFiltered}
                headers={headerWithFormatter.map(d => ({
                  ...d,
                  rowHeaderBackgroundColor: '#eee',
                }))}
                altBgColor="#ebebeb"
                render={virtualizedGridRenderer({
                  autoFixColByKey: false,
                  rowHeaderRender,
                  height: height - 40,
                  width: width - 50,
                  cellRender: statusRender,
                  headerRowHeight: 30,
                })}
                fuzzyFilter={fuzzyFilter}
                sortOptions={[utilityHeader, unitHeader]}
              />
            </Content>
          ) : (
            <Content style={{ height: '200px' }}>
              <Empty
                imageStyle={{ paddingTop: '30px' }}
                description={
                  selectedUnits && selectedUnits.length <= 0
                    ? 'Please select units'
                    : 'Please click on retrieve button to retrieve data'
                }
              />
            </Content>
          )
        }}
      </FullHeightSizer>
    </div>
  )
}

const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  units: getUnits(state),
  validYearRange: getValidYearRange(state),
})

export default connect(mapStateToProps)(PjmLevel2ValStatus)
